import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api, formatMoney, LOCATIONS, LOCATION_LABEL } from "@/lib/api";
import { toast } from "sonner";

export default function StockMovementDialog({
    open,
    onOpenChange,
    type,
    products,
    defaultProductId,
    onDone,
}) {
    const [productId, setProductId] = useState("");
    const [quantity, setQuantity] = useState("");
    const [unitPrice, setUnitPrice] = useState("");
    const [note, setNote] = useState("");
    const [location, setLocation] = useState("club");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setProductId(defaultProductId || "");
            setQuantity("");
            setUnitPrice("");
            setNote("");
            setLocation("club");
        }
    }, [open, defaultProductId]);

    useEffect(() => {
        // Autofill unit price: purchase_price for IN, sale_price for OUT
        const p = products.find((x) => x.id === productId);
        if (p) {
            const suggested = type === "IN" ? p.purchase_price : p.sale_price;
            setUnitPrice(String(suggested ?? 0));
        }
    }, [productId, type]); // eslint-disable-line

    const isIn = type === "IN";
    const total = Number(quantity || 0) * Number(unitPrice || 0);

    const submit = async () => {
        if (!productId) return toast.error("Choisissez un produit");
        const q = parseInt(quantity, 10);
        const up = parseFloat(unitPrice);
        if (!q || q <= 0) return toast.error("Quantité invalide");
        if (isNaN(up) || up < 0) return toast.error("Prix invalide");

        setLoading(true);
        try {
            await api.post("/transactions", {
                type,
                product_id: productId,
                quantity: q,
                unit_price: up,
                location,
                note,
            });
            toast.success(isIn ? "Entrée enregistrée" : "Sortie enregistrée");
            onOpenChange(false);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={`stock-${type.toLowerCase()}-dialog`}
                className="sm:max-w-lg"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                        {isIn ? "Nouvelle entrée de stock" : "Nouvelle sortie de stock"}
                    </DialogTitle>
                    <DialogDescription>
                        {isIn
                            ? "Achat / réapprovisionnement. Le montant sera retiré du coffre."
                            : "Vente / retrait. Le montant sera ajouté au coffre."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Produit
                        </Label>
                        <Select value={productId} onValueChange={setProductId}>
                            <SelectTrigger
                                data-testid="movement-product-select"
                                className="mt-2 h-11"
                            >
                                <SelectValue placeholder="Choisissez un produit" />
                            </SelectTrigger>
                            <SelectContent>
                                {products.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.name} — stock {p.quantity}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Quantité
                            </Label>
                            <Input
                                data-testid="movement-quantity-input"
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Prix unitaire
                            </Label>
                            <Input
                                data-testid="movement-price-input"
                                type="number"
                                min="0"
                                step="0.01"
                                value={unitPrice}
                                onChange={(e) => setUnitPrice(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Emplacement du coffre
                        </Label>
                        <Select value={location} onValueChange={setLocation}>
                            <SelectTrigger
                                data-testid="movement-location-select"
                                className="mt-2 h-11"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {LOCATIONS.map((l) => (
                                    <SelectItem key={l.key} value={l.key}>
                                        {l.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Note (optionnel)
                        </Label>
                        <Textarea
                            data-testid="movement-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="mt-2"
                            rows={2}
                            placeholder="Client, fournisseur, commentaire…"
                        />
                    </div>

                    <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
                        <span className="text-sm text-slate-600">
                            {isIn ? "À payer depuis" : "À encaisser dans"}{" "}
                            <span className="font-semibold text-slate-900">
                                {LOCATION_LABEL[location]}
                            </span>
                        </span>
                        <span
                            className={`font-display font-semibold text-xl tabular-nums ${
                                isIn ? "text-rose-700" : "text-emerald-700"
                            }`}
                        >
                            {isIn ? "−" : "+"}
                            {formatMoney(total)}
                        </span>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        data-testid="movement-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={loading}
                        data-testid="movement-submit-btn"
                        className={
                            isIn
                                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                                : "bg-rose-600 hover:bg-rose-700 text-white"
                        }
                    >
                        {loading ? "Enregistrement…" : isIn ? "Valider l'entrée" : "Valider la sortie"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
